import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
}

/**
 * Cmd-K / Ctrl-K command palette. Lazy-loads the dashboard rows once and lets you
 * jump straight to a client. Also exposes route shortcuts (dashboard, reports, audit, users).
 */
export function CommandPalette() {
  const [, setLocation] = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { user, signOut } = useAuth();

  // Global hotkey
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    if (!open) setQ('');
  }, [open]);

  const { data } = useQuery({
    queryKey: ['dashboard-mini'],
    queryFn: () => api<{ data: Array<{ clientId: string; name: string; segment: string | null }> }>('/api/dashboard'),
    enabled: open,
    staleTime: 60_000,
  });

  const items: PaletteItem[] = useMemo(() => {
    const out: PaletteItem[] = [];
    if (can(user?.role, 'client.view')) {
      out.push({ id: 'go-dashboard', label: 'Go to Dashboard', hint: 'Adviser home', run: () => { setLocation('/'); setOpen(false); } });
    }
    if (can(user?.role, 'audit.read') || can(user?.role, 'export.run')) {
      out.push({ id: 'go-reports', label: 'Open Reports', hint: 'reviews-due · compliance · advice', run: () => { setLocation('/reports'); setOpen(false); } });
    }
    if (can(user?.role, 'audit.read')) {
      out.push({ id: 'go-audit', label: 'Open Audit log', run: () => { setLocation('/audit'); setOpen(false); } });
    }
    if (can(user?.role, 'users.manage')) {
      out.push({ id: 'go-users', label: 'Manage users', run: () => { setLocation('/users'); setOpen(false); } });
    }
    out.push({ id: 'sign-out', label: 'Sign out', run: () => { void signOut(); setOpen(false); } });

    // Client jumps
    for (const c of data?.data ?? []) {
      out.push({
        id: `client-${c.clientId}`,
        label: `Open client · ${c.name}`,
        hint: c.segment ? `Segment ${c.segment}` : 'Unsegmented',
        run: () => { setLocation(`/clients/${c.clientId}`); setOpen(false); },
      });
    }
    return out;
  }, [data, user, setLocation, signOut]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items.slice(0, 8);
    const needle = q.toLowerCase();
    return items.filter(it => it.label.toLowerCase().includes(needle)).slice(0, 30);
  }, [items, q]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-ink/30" onClick={() => setOpen(false)}>
      <div className="w-full max-w-xl bg-paper border border-hairline qa-shadow" onClick={e => e.stopPropagation()}>
        <input ref={inputRef}
          className="qa-input border-0 border-b border-hairline rounded-none text-base"
          placeholder="Jump to client, page, or action…"
          value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && filtered[0]) filtered[0].run(); }}
        />
        <ul className="max-h-96 overflow-y-auto">
          {filtered.map(it => (
            <li key={it.id}>
              <button className="w-full text-left px-4 py-2 hover:bg-rust/5 flex justify-between items-center"
                onClick={it.run}>
                <span className="text-ink">{it.label}</span>
                {it.hint && <span className="text-xs text-ink-mute qa-mono">{it.hint}</span>}
              </button>
            </li>
          ))}
          {filtered.length === 0 && <li className="px-4 py-6 text-center text-ink-mute text-sm">No matches.</li>}
        </ul>
        <div className="border-t border-hairline px-4 py-2 text-xs text-ink-mute flex justify-between qa-mono">
          <span>↵ open · esc close</span>
          <span>⌘K toggle</span>
        </div>
      </div>
    </div>
  );
}
